import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../redux/axios";

const initialState = {
  vendorData: { data: [], currentPage: 0, totalPages: 0, totalItems: 0 },
  isFetchingVendorData: false,
  isAddingVendorData: false,
  isEditingVendorData: false,
  isDeletingVendorData: false,
  error: null,
  vendorSmscData: [],
  isFetchingVendorSmscList: false,
  isFetchingUserBalance: false,
  isFetchingVendorTxns: false,
};

const normalizeVendorDateTime = (value, isEndDate = false) => {
  if (!value) return "";
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return "";

  const normalized = trimmed.replace(" ", "T");
  const [datePart, timePartRaw] = normalized.split("T");
  if (!timePartRaw) {
    return `${datePart}T${isEndDate ? "23:59:59.999" : "00:00:00.000"}`;
  }

  const [timePart, msPartRaw] = timePartRaw.split(".");
  const timeSegments = timePart.split(":");
  const hours = timeSegments[0] || "00";
  const minutes = timeSegments[1] || "00";
  const seconds = timeSegments[2] || "00";
  const baseTime = `${hours}:${minutes}:${seconds}`;
  const msPart = (msPartRaw || (isEndDate ? "999" : "000"))
    .padEnd(3, "0")
    .slice(0, 3);

  return `${datePart}T${baseTime}.${msPart}`;
};

export const getVendorData = createAsyncThunk(
  "vendor/getData",
  async (params = {}, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      const {
        pageNumber = 0,
        pageSize = 10,
        sortBy = "id",
        sortDirection = "ASC",
        searchTerm,
        startDate,
        endDate,
        toGmt,
      } = params;

      let url = `/smsc/vendors/list?pageNumber=${pageNumber}&pageSize=${pageSize}&sortBy=${sortBy}&sortDirection=${sortDirection.toUpperCase()}`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      const normalizedStartDate = normalizeVendorDateTime(startDate, false);
      const normalizedEndDate = normalizeVendorDateTime(endDate, true);

      if (normalizedStartDate) {
        url += `&startDate=${encodeURIComponent(normalizedStartDate)}`;
      }
      if (normalizedEndDate) {
        url += `&endDate=${encodeURIComponent(normalizedEndDate)}`;
      }
      if (toGmt) {
        url += `&toGmt=${encodeURIComponent(toGmt)}`;
      }

     

      const { data, status } = await axios.get(url, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });


      if (status.toString().startsWith("2")) {
        return {
          data: data.data || [],
          currentPage: data.currentPage || 0,
          totalPages: data.totalPages || 0,
          totalItems: data.totalItems || 0,
        };
      } else {
        console.error("API error response:", data);
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("API error:", err.response?.data || err.message);

  
      if (err.response?.status === 404) {
        return thunkAPI.rejectWithValue(
          err.response?.data || { message: "No vendors found", status: 404, error: "Not Found" }
        );
      }

      return thunkAPI.rejectWithValue(
        err.response?.data || { message: "Network error" }
      );
    }
  }
);

export const addVendorData = createAsyncThunk(
  "vendor/createData",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const { data, status } = await axios.post("/smsc/vendors/create", udata, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const editVendorData = createAsyncThunk(
  "vendor/editData",
  async ({ id, udata }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      const { data, status } = await axios.put(
        `/smsc/vendors/update/${id}`,
        udata,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const deleteVendorData = createAsyncThunk(
  "vendor/deleteData",
  async ({ udata }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      const { data, status } = await axios.delete(
        `/smsc/vendors/delete/${udata}`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const vendorSmscList = createAsyncThunk(
  "vendor/user-list",
  async ({ udata }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let url = `/smsc/vendors/smsc-by-vendor/${udata}`;
      const { data, status } = await axios.get(url, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const modifyVendorBalance = createAsyncThunk(
  "vendor/modify-balance",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const queryParam = new URLSearchParams();
      if (udata.vendorId) queryParam.set("vendorId", udata.vendorId);
      if (udata.type) queryParam.set("type", udata.type);
      if (udata.amount) queryParam.set("amount", udata.amount);
      if (udata.walletFlag) queryParam.set("walletFlag", udata.walletFlag);
      let url = `/companies/balance?${queryParam.toString()}`;

      const { data, status } = await axios.put(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const updateFlag = createAsyncThunk(
  "vendor/flag",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let url = `/companies/refresh-flag?param=vendor`;

      const { data, status } = await axios.post(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const getCTxnHistory = createAsyncThunk(
  "vendor/transaction-history",
  async ({ vendorId, startDate, endDate, statusType, types }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let url = "/companies/report";

      const params = new URLSearchParams();
      if (vendorId) params.append("vendorIds", vendorId);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (statusType) params.append("status", statusType);
      if (types) params.append("type", types);

      const finalUrl = `${url}?${params.toString()}`;
      const { data, status } = await axios.get(finalUrl, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

const vendorSlice = createSlice({
  name: "vendor",
  initialState,
  reducers: {
    clearStateVendor: (state) => {
      state.vendorData = {
        data: [],
        currentPage: 0,
        totalPages: 0,
        totalItems: 0,
      };
      state.isFetchingVendorData = false;
      state.isAddingVendorData = false;
      state.isEditingVendorData = false;
      state.isDeletingVendorData = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
   
    builder
      .addCase(getVendorData.pending, (state) => {
        state.isFetchingVendorData = true;
        state.error = null;
      })
      .addCase(getVendorData.fulfilled, (state, action) => {
        state.isFetchingVendorData = false;
        state.vendorData = {
          data: action.payload.data || [],
          currentPage: action.payload.currentPage || 0,
          totalPages: action.payload.totalPages || 0,
          totalItems: action.payload.totalItems || 0,
        };
      })
      .addCase(getVendorData.rejected, (state, action) => {
        state.isFetchingVendorData = false;
        state.error = action.payload || "Failed to fetch vendor data.";

        if (
          action.payload?.status === 404 ||
          action.payload?.error === "Not Found"
        ) {
          state.vendorData = {
            data: [],
            currentPage: 0,
            totalPages: 0,
            totalItems: 0,
          };
        }
      });

    builder
      .addCase(addVendorData.pending, (state) => {
        state.isAddingVendorData = true;
        state.error = null;
      })
      .addCase(addVendorData.fulfilled, (state, action) => {
        state.isAddingVendorData = false;
        
      })
      .addCase(addVendorData.rejected, (state, action) => {
        state.isAddingVendorData = false;
        state.error = action.payload || "Failed to add vendor data.";
      });

    builder
      .addCase(editVendorData.pending, (state) => {
        state.isEditingVendorData = true;
        state.error = null;
      })
      .addCase(editVendorData.fulfilled, (state, action) => {
        state.isEditingVendorData = false;
       
      })
      .addCase(editVendorData.rejected, (state, action) => {
        state.isEditingVendorData = false;
        state.error = action.payload || "Failed to edit vendor data.";
      });

    builder
      .addCase(deleteVendorData.pending, (state) => {
        state.isDeletingVendorData = true;
        state.error = null;
      })
      .addCase(deleteVendorData.fulfilled, (state, action) => {
        state.isDeletingVendorData = false;
      
      })
      .addCase(deleteVendorData.rejected, (state, action) => {
        state.isDeletingVendorData = false;
        state.error = action.payload || "Failed to delete vendor data.";
      });

    builder
      .addCase(vendorSmscList.pending, (state) => {
        state.isFetchingVendorSmscList = true;
        state.error = null;
      })
      .addCase(vendorSmscList.fulfilled, (state, action) => {
        state.isFetchingVendorSmscList = false;
        state.vendorSmscData = action.payload.data || [];
      })
      .addCase(vendorSmscList.rejected, (state, action) => {
        state.isFetchingVendorSmscList = false;
        state.error = action.payload || "Failed to fetch vendor user list.";
      });

    builder
      .addCase(modifyVendorBalance.pending, (state) => {
        state.isFetchingUserBalance = true;
        state.error = null;
      })
      .addCase(modifyVendorBalance.fulfilled, (state, action) => {
        state.isFetchingUserBalance = false;
        state.vendorSmscData = action.payload.data || [];
      })
      .addCase(modifyVendorBalance.rejected, (state, action) => {
        state.isFetchingUserBalance = false;
        state.error = action.payload || "Failed to modify vendor balance.";
      });

    builder
      .addCase(getCTxnHistory.pending, (state) => {
        state.isFetchingVendorTxns = true;
        state.error = null;
      })
      .addCase(getCTxnHistory.fulfilled, (state, action) => {
        state.isFetchingVendorTxns = false;
      })
      .addCase(getCTxnHistory.rejected, (state, action) => {
        state.isFetchingVendorTxns = false;
        state.error = action.payload || "Failed to get vendor transactions.";
      });
  },
});

export const { clearStateVendor } = vendorSlice.actions;
export const vendorSelector = (state) => state.vendor;
export default vendorSlice.reducer;
